import { Component, inject, input } from '@angular/core';
import { ZiroFieldAbstractDirective } from '../../ziro-field-abstract.directive';
import { ZiroFormsModule } from '../../ziro-forms.module';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ZiroFieldComponentConfig, ZiroFormsMessage, ZiroFormsValidationState } from '../../ziro-forms-models';
import { isNil } from 'lodash';
import { ButtonStyles } from '../../../button/button.component';
import { FormsModule } from '@angular/forms';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { PipesModule } from '../../../shared/pipes/pipes.module';

export class FileUploadBoxConfig extends ZiroFieldComponentConfig {
  constructor(
    public config: {
      maxSize: `${number}MB`;
      defaultFileTypes: `.${string}`[];
      multipleFiles?: boolean;
      title: string;
      body: string;
      buttonIcon: ZiroIcons;
      buttonLabel: string;
    },
  ) {
    super();
  }
}

@Component({
  selector: 'ziro-file-upload-box',
  templateUrl: 'file-upload-box.component.html',
  styleUrls: ['file-upload-box.component.scss'],
  imports: [CommonModule, TranslateModule, ZiroFormsModule, FormsModule, PipesModule],
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: FileUploadBoxComponent }],
})
export class FileUploadBoxComponent extends ZiroFieldAbstractDirective<File[], File[], FileUploadBoxConfig> {
  ButtonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  ZiroFormsValidationState = ZiroFormsValidationState;

  readonly cardClasses = input<string>();

  private _translateService = inject(TranslateService);

  protected validationInProgressMessage = '';

  maxSizeInMb: number;
  defaultFileTypes: string;
  multipleFiles: boolean;
  title: string;
  body: string;
  buttonIcon: ZiroIcons;
  buttonLabel: string;
  isFileHovering = false;

  private _validators = {
    isWithinSizeConstraint: {
      validator: (val: File[]): ZiroFormsValidationState => {
        return !val || val.reduce((acc, curr) => acc + curr.size, 0) <= this.maxSizeInMb * 1000000
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID;
      },
      message: (): ZiroFormsMessage => {
        return {
          content: 'tkey;file_upload_box.validation.file_size',
          params: {
            fileSize: this.getHumanReadableFileSize(this.maxSizeInMb * 1000000),
          },
        };
      },
    },
  };

  applyComponentConfig = ({ config }: FileUploadBoxConfig): void => {
    this.maxSizeInMb = isNil(config?.maxSize) ? this.maxSizeInMb : Number(config.maxSize.replace('MB', ''));
    this.defaultFileTypes = isNil(config?.defaultFileTypes) ? this.defaultFileTypes : config.defaultFileTypes.join(',');
    this.multipleFiles = isNil(config?.multipleFiles) ? this.multipleFiles : config.multipleFiles;
    this.title = isNil(config?.title) ? this.title : config.title;
    this.body = isNil(config?.body) ? this.body : config.body;
    this.buttonIcon = isNil(config?.buttonIcon) ? this.buttonIcon : config.buttonIcon;
    this.buttonLabel = isNil(config?.buttonLabel)
      ? this.buttonLabel
      : this._translateService.instant(config.buttonLabel);

    if (!this.config?.validation?.includes(this._validators.isWithinSizeConstraint)) {
      this.config.validation = [this._validators.isWithinSizeConstraint, ...(this.config?.validation || [])];
    }
  };

  onFileUpload($event: Event): void {
    const target = $event.target as HTMLInputElement;
    this._handleFiles(target.files);
    // Ensures input is ready for the next upload
    target.value = '';
  }

  getHumanReadableFileSize(fileSizeInBytes: number): string {
    let fileSize = fileSizeInBytes;
    const possibleUnits = ['B', 'kB', 'MB', 'GB'];
    let unit = 'B';

    while (fileSize >= 1000 && unit !== possibleUnits[possibleUnits.length]) {
      fileSize /= 1000;
      unit = possibleUnits[possibleUnits.indexOf(unit) + 1];
    }

    return `${Math.round(fileSize * 10) / 10} ${unit}`;
  }

  removeFile(file: File): void {
    const newFieldData = this.fieldData.filter((f) => f !== file);
    this.updateSelf(newFieldData);
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      this.isFileHovering = true;
    }
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isFileHovering = false;
  }

  dragDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragLeave(event);
    const files = event.dataTransfer?.files;
    if (!files) {
      return;
    }

    this._handleFiles(files);
  }

  private _handleFiles(fileList: FileList): void {
    if (!fileList.length) {
      return;
    }
    if (this.multipleFiles) {
      const filesArr: File[] = [];
      if (this.fieldData) {
        // keep existing files
        filesArr.push(...this.fieldData);
      }
      for (let i = 0; i < fileList.length; i++) {
        filesArr.push(fileList.item(i));
      }
      this.updateSelf(filesArr);
    } else {
      const file = fileList.item(0);
      this.updateSelf([file]);
    }
  }

  protected toEntity = (fieldData: File[]): File[] => {
    return fieldData;
  };

  protected toFieldData = (entity: File[]): File[] => {
    return entity;
  };
}
